import React, { useContext, useEffect, useState } from 'react';
import { ProviderProductsContext } from '../contexts/ProviderProductsContext';
import productsPathTo from '../utils/Api/productsPathTo';
import { useNavigate } from 'react-router-dom';
import { Switch, Typography, Card, Stack, CardMedia, CardContent, CardActionArea, CardActions, FormControl, FormControlLabel, Box, ButtonBase, Button } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { currency } from './building_blocks/Currency';
import SnackbarContext from '../contexts/SnackbarContext';
import { apiBaseUrl } from '../utils/Api/Api';
import ProductPhotoPlaceholder from '../images/placeholder_product_image.png';
import ProductsTagFilters from './ProductsTagFilters';
import ProductPriceChangeDialog from './ProductPriceChangeDialog';
import { NewProductDialog } from '.';
import { getTagMatchCount } from '../utils/rankProductsByTags';
import { useProductTagFilter } from '../hooks/useProductTagFilter';

// Funções de ordenação base
const sortByOldest = (a, b, direction) => (direction === 'asc' ? a.id - b.id : b.id - a.id);
const sortByTitle = (a, b, direction) => (direction === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title));
const sortByPrice = (a, b, direction) => (direction === 'asc' ? a.price - b.price : b.price - a.price);
const sortByPublished = (a, b, direction) => {
  if (a.published === b.published) return 0;
  return direction === 'asc' ? (a.published ? -1 : 1) : (a.published ? 1 : -1);
};

const ProviderProductList = ({provider}) => {
  const [selectedSort, setSelectedSort] = useState('oldest');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' ou 'desc'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);

  const navigate = useNavigate();
  const { addProviderProduct, providerProductList, updateProviderProductList, updateProviderProduct } = useContext(ProviderProductsContext);
  const { tagFilter, setTagFilter, catalogTags, filteredProducts } = useProductTagFilter(providerProductList);
  
  const { openSnackbar, openConfirmDialog } = useContext(SnackbarContext);

  useEffect(() => {
    if (providerProductList.length === 0) {
      productsPathTo.list().then((response) => {
        updateProviderProductList(response.data)
      })
    }
  }, [])

  const handlePublishedChange = async (event, product) => {
    // ainda não está smooth. até o retorno da chamada de atualização, a lista de produtos ainda não foi atualizada. Mas está funcionando
    event.preventDefault();
    const { checked } = event.target;
    if (product.been_published === false && checked === true) {
      openConfirmDialog(
        "Tem certeza que deseja publicar este produto? Após publicação, o título não poderá ser alterado.",
        async () => {
          try {
            const updatedProduct = await productsPathTo.update_published(product.id, { published: checked });
            updateProviderProduct(updatedProduct.data);
            checked === true ? openSnackbar("Produto publicado na loja!", "success") : openSnackbar("Produto retirado da loja!", "success")
          } catch (error) {
            console.error('Error updating product:', error);
          }
        }
      )
    } else {
      try {
        const updatedProduct = await productsPathTo.update_published(product.id, { published: checked });
        updateProviderProduct(updatedProduct.data);
        checked === true ? openSnackbar("Produto publicado na loja!", "success") : openSnackbar("Produto retirado da loja!", "success")
      } catch (error) {
        console.error('Error updating product:', error);
      }
    }
  };

  const sortOptions = {
    oldest: (a, b) => sortByOldest(a, b, sortDirection),
    title: (a, b) => sortByTitle(a, b, sortDirection),
    price: (a, b) => sortByPrice(a, b, sortDirection),
    published: (a, b) => sortByPublished(a, b, sortDirection),
  };

  // Função para lidar com a mudança de ordenação
  const handleSortChange = (sortType) => {
    if (selectedSort === sortType) {
      // Se clicar no mesmo botão, alternar a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Se mudar o tipo de ordenação, manter a direção padrão (ascendente)
      setSelectedSort(sortType);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (tagFilter.length > 0) {
      const matchDiff = getTagMatchCount(b, tagFilter) - getTagMatchCount(a, tagFilter);
      if (matchDiff !== 0) return matchDiff;
    }
    return sortOptions[selectedSort](a, b);
  });

  const handleDialogClose = (updatedProduct = null) => {
    if (updatedProduct) {
      openSnackbar("Preço alterado com sucesso!", "success");
      updateProviderProduct(updatedProduct);
    }
    setSelectedProduct(null);
  }

  const openChangePriceDialog = (product) => {
    setSelectedProduct(product);
  }

  const handleNewProductClick = () => {
    setShowNewProductDialog(true)
  };

  const handleNewProductDialogOnClose = (product) => {
    setShowNewProductDialog(false);
    
    if (product && product.id) {
      addProviderProduct(product);
      navigate(`/provider/products/${product.id}`);
    }
  };

  return (
    <>
      <ProductsTagFilters
        tagFilter={tagFilter}
        setTagFilter={setTagFilter}
        tags={catalogTags}
        numProducts={filteredProducts.length}
      />

      <Stack spacing={1}>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'right', gap: 1 }}>
          {[
            { label: '#', value: 'oldest' },
            { label: 'Título', value: 'title' },
            { label: 'Preço', value: 'price' },
            { label: 'Publicado', value: 'published' },
          ].map((option) => (
            <ButtonBase
              key={option.value}
              onClick={() => handleSortChange(option.value)}
              sx={{
                px: 1,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant="label">{option.label}</Typography>
              {selectedSort === option.value &&
                (sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
            </ButtonBase>
          ))}
        </Box>

        <ProductPriceChangeDialog selectedProduct={selectedProduct} onClose={handleDialogClose}/>

        {sortedProducts
          .map((product, index) => (
            <Card variant="outlined" key={product.id} sx={{ display: 'flex', width: '100%' }}>
            <CardActionArea
              sx={{ display: 'flex', flexDirection: 'row', flex: 1, minWidth: 0 }} // minWidth: 0 permite encolher
              onClick={() => navigate(`/provider/products/${product.id}`)}
              title={product.title}
            >
              <CardMedia
                component="img"
                image={product.item_photo ? `${apiBaseUrl}${product.item_photo}` : ProductPhotoPlaceholder}
                alt={product.title}
                title={product.title}
                sx={{ width: '64px', maxHeight: '64px', flexShrink: 0 }} // Não encolhe
              />
              <CardContent
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  flexShrink: 1,
                  minWidth: 0, // Evita overflow do conteúdo
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px', // Reduz padding se necessário
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    flexGrow: 1,
                    flexShrink: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis', // Adiciona "..." se cortar
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.title}
                </Typography>
              </CardContent>
            </CardActionArea>
            <CardActions sx={{ paddingY: 0, paddingX: '4px', margin: 0, flexShrink: 0 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexShrink: 0, margin: 'auto', padding: '22px 2px', m: 0, cursor: 'pointer', borderRadius: '4px', textDecoration: 'underline' }}
                component={ButtonBase}
                onClick={() => openChangePriceDialog(product)}
              >
                {currency(product.price)}
              </Typography>

              <FormControl>
                <FormControlLabel
                  label="Publicar"
                  sx={{ flexDirection: 'column', margin: 0 }}
                  control={
                    <Switch
                      checked={product.published}
                      onChange={(event) => handlePublishedChange(event, product)}
                      color="primary"
                    />
                  }
                />
              </FormControl>
            </CardActions>
          </Card>
        ))}
      </Stack>
      <Button
          variant="outlined"
          color="primary"
          onClick={() => handleNewProductClick()}
        >
          Criar novo produto
        </Button>
        <NewProductDialog showDialog={showNewProductDialog} onClose={handleNewProductDialogOnClose} />
    </>
  )
  
}

export default ProviderProductList
